<template>
  <div>
    <h2 class="content-block">Approve Adjustments</h2>

    <div>
      <div class="dx-fieldset">
        <div class="dx-field">Click an adjustment to review.</div>
      </div>
      <dx-data-grid
        ref="adjustments-grid"
        :data-source="adjustmentDataSource"
        :selection="{ mode: 'single' }"
        :show-borders="true"
        :key-expr="'adjustment.id'"
        :remote-operations="false"
        :allow-column-reordering="false"
        :allow-column-resizing="true"
        :row-alternation-enabled="true"
        :hover-state-enabled="true"
        :word-wrap-enabled="true"
        :column-auto-width="true"
        :show-row-lines="true"
        :show-column-lines="true"
        class="dx-card wide-card"
        width="100%"
        @selection-changed="onAdjustmentSelectionChanged"
      >
        <dx-editing>
          <dx-texts confirm-delete-message="" />
        </dx-editing>
        <dx-column-chooser :enabled="false" />
        <dx-export
          :enabled="true"
          :file-name="`${currentDate}-approve-adjustments`"
        />
        <dx-paging :page-size="pageSizes[0]" />
        <dx-pager
          :allowed-page-sizes="pageSizes"
          :show-page-size-selector="true"
          :show-navigation-buttons="true"
          :show-info="true"
        />
        <dx-filter-row :visible="true" />
        <dx-header-filter :visible="false" />
        <dx-filter-panel :visible="true" />
        <dx-column
          data-field="adjustment.id"
          caption="Adj. Id"
          :format="config.idFormat"
        />
        <dx-column
          data-field="match.id"
          caption="Match Id"
          :format="config.idFormat"
        />
        <dx-column
          data-field="adjustment.gateway"
          :lookup="gatewayColumnLookup"
          caption="Gateway"
        />
		<dx-column
          data-field="adjustment.source"
          :lookup="comparisonSourceTypeColumnLookup"
          caption="Source"
        />
        <dx-column
          data-field="adjustment.currencyId"
          :lookup="currencyColumnLookup"
          caption="Currency"
        />
        <dx-column
          data-field="adjustment.amount"
          caption="Amount"
          :format="config.financialNumberFormat"
        />
        <dx-column
          data-field="adjustment.timestamp"
          data-type="datetime"
          :format="config.intlDateTimeFormat"
          caption="Timestamp"
        />
        <dx-column data-field="adjustment.comment" />
        <dx-column
          data-field="adjustment.created"
          data-type="datetime"
          :format="config.intlDateTimeFormat"
          caption="Created"
        />
        <dx-column
          data-field="adjustment.userIdCreated"
          :lookup="userColumnLookup"
          caption="Created By"
        />
      </dx-data-grid>

      <reviews-popup
        :show-popup.sync="reviewsVisible"
        :row-id="reviewRowId"
        :reviews="reviews"
      />

      <div v-if="showDetails">
        <dx-data-grid
          :data-source="selected.rows"
          :show-borders="true"
          :key-expr="'row.id'"
          :remote-operations="false"
          :allow-column-reordering="false"
          :allow-column-resizing="true"
          :row-alternation-enabled="true"
          :hover-state-enabled="true"
          :word-wrap-enabled="true"
          :column-auto-width="true"
          :show-row-lines="true"
          :show-column-lines="true"
          class="dx-card wide-card"
          width="100%"
          :on-cell-click="onCellClickRows"
        >
          <dx-export
            :enabled="true"
            :file-name="`${currentDate}-approve-adjustments-rows`"
          />
          <dx-paging :page-size="pageSizes[0]" />
          <dx-pager
            :allowed-page-sizes="pageSizes"
            :show-page-size-selector="true"
            :show-navigation-buttons="true"
            :show-info="true"
          />
          <dx-filter-row :visible="true" />
          <dx-header-filter :visible="false" />
          <dx-filter-panel :visible="true" />
          <dx-column data-field="row.id" caption="Id" />
          <dx-column
            data-field="row.gateway"
            :lookup="gatewayColumnLookup"
            caption="Gateway"
          />
		  <dx-column
            data-field="row.source"
            :lookup="comparisonSourceTypeColumnLookup"
            caption="Source"
          />
          <dx-column data-field="row.idAtComparisonSource" caption="CS Id" />
          <dx-column data-field="row.idAtGateway" caption="Gateway Id" />
          <dx-column
            data-field="row.timestamp"
            data-type="datetime"
            :format="config.intlDateTimeFormat"
          />
          <dx-column
            data-field="row.amount"
            caption="Amount"
            :format="config.financialNumberFormat"
          />
          <dx-column
            data-field="row.rowType"
            :lookup="rowTypeColumnLookup"
            caption="Type"
          />
          <dx-column
            data-field="row.reversal"
            caption="Reversal"
            true-text="Yes"
            false-text="No"
          />
          <dx-column data-field="row.counterParty" caption="Counter Party" />
          <dx-column
            data-field="reviews.length"
            caption="Reviews"
            :format="config.countFormat"
          />
        </dx-data-grid>

        <div class="dx-fieldset">
          <div class="dx-fieldset-header">Actions</div>
          <div class="dx-field">
            <div class="dx-field-label">
              <DxButton
                :disabled="selected.adjustment.userIdCreated === profile.id"
                text="Approve"
                @click="approveAdjustment"
              />
            </div>
            <div v-if="selected.adjustment.userIdCreated === profile.id">
              Cannot approve as you created the adjustment
            </div>
          </div>
          <div class="dx-field">
            <div class="dx-field-label">
              <DxButton text="Reject" @click="rejectAdjustment" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState} from 'vuex'
import {DxColumn, DxColumnChooser, DxDataGrid, DxEditing, DxExport, DxFilterPanel, DxFilterRow, DxHeaderFilter, DxPager, DxPaging, DxTexts,} from 'devextreme-vue/data-grid'
import {DxButton} from 'devextreme-vue/button'
import {DxTagBox} from 'devextreme-vue'
import {http} from '@/utils/http'
import {show} from '@/utils/show'
import {config} from '@/config'
import {showHttpError} from '../../utils/http'
import ReviewsPopup from '@/components/reviews-popup'

export default {
  name: 'ViewMatch',
  components: {
    DxColumn,
    DxColumnChooser,
    DxDataGrid,
    DxEditing,
    DxExport,
    DxFilterRow,
    DxFilterPanel,
    DxHeaderFilter,
    DxPager,
    DxPaging,
    DxButton,
    DxTexts,
    // eslint-disable-next-line vue/no-unused-components
    DxTagBox,
    ReviewsPopup,
  },
  data() {
    return {
      config,

      pageSizes: config.itemsPerPage,

      adjustmentDataSource: null,

      showDetails: false,
      selected: {
        adjustment: {
          id: null,
        },
        rows: [],
      },

      reviewsVisible: false,
      reviewRowId: -1,
      reviews: [],
    }
  },
  computed: {
    ...mapState('user', [
      'profile',
      'comparisonSourceColumnLookup',
      'currencyColumnLookup',
      'userColumnLookup',
      'rowTypeColumnLookup',
	  'gatewayColumnLookup',
	  'comparisonSourceTypeColumnLookup'
    ]),
    currentDate() {
      return new Date().toISOString().split('T')[0]
    },
  },
  created() {
    this.loadData()
  },
  destroyed() {},
  methods: {
    async loadData() {
      const openAdjustments = await http
        .get('reconciliation/admin/adjustment/open')
        .json()
      this.adjustmentDataSource = openAdjustments.adjustments
    },
    onAdjustmentSelectionChanged({ selectedRowsData }) {
      const data = selectedRowsData[0]

      this.showDetails = !!data
      if (this.showDetails) {
        this.selected = data
      }
    },
    rejectAdjustment() {
      http
        .post('reconciliation/admin/match/delete', {
          json: {
            id: this.selected.match.id,
          },
        })
        .then(() => {
          show.success('Adjustment rejected (my deleting the match)')
        })
        .catch((e) => {
          showHttpError(e)
        })
      this.removeSelectedAdjustment()
    },
    approveAdjustment() {
      http
        .post('reconciliation/admin/adjustment/approve', {
          json: {
            id: this.selected.adjustment.id,
          },
        })
        .then(() => {
          show.success('Adjustment approved')
        })
        .catch((e) => {
          showHttpError(e)
        })
      this.removeSelectedAdjustment()
    },
    removeSelectedAdjustment() {
      const grid = this.$refs['adjustments-grid'].instance
      const rowIndex = grid.getRowIndexByKey(this.selected.adjustment.id)
      grid.deleteRow(rowIndex)
    },
    onCellClickRows(e) {
      if (e.column.caption === 'Reviews' && e.rowType === 'data') {
        this.reviewsVisible = true
        this.reviewRowId = e.row.data.row.id
        this.reviews = e.row.data.reviews
      }
    },
  },
}
</script>
<style lang="scss">
td[role='columnheader'] {
  text-align: center !important;
}
</style>
